import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Check, Tv, Smartphone, Tablet, Monitor, ChevronDown, ChevronUp,
  Calendar, Sparkles, Star, ShieldCheck, Users, ArrowRight, Gift, HelpCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const eur = (n) => {
  const v = Number(n || 0);
  return (Number.isInteger(v) ? String(v) : v.toFixed(2)).replace('.', ',');
};

// Descriptions commerciales par badge/package
const PACKAGE_DESCRIPTIONS = {
  'Découverte': "Idéal pour tester le service sans engagement long.",
  'Flexible': "Une solution pratique pour profiter du service sur une période intermédiaire.",
  'Populaire': "L'offre la plus équilibrée pour profiter du service toute l'année.",
  'Offre Famille': "Parfait pour plusieurs utilisateurs avec des connexions simultanées.",
  'Premium': "Une solution complète pour profiter longtemps du service avec plusieurs connexions.",
  'Meilleur choix': "Le meilleur compromis durée / prix pour un usage prolongé.",
  'Prix le plus bas / mois': "Le meilleur tarif mensuel pour économiser sur la durée.",
};

const FAQ_ITEMS = [
  {
    q: "Quelle durée d'abonnement IPTV choisir ?",
    a: "Cela dépend de votre besoin. Une durée courte permet de tester le service, tandis qu'un abonnement 1 an, 2 ans ou 3 ans permet d'obtenir un meilleur prix mensuel.",
  },
  {
    q: "Que signifie une connexion simultanée ?",
    a: "Une connexion simultanée correspond à un écran utilisé au même moment. Une offre avec 3 connexions permet donc d'utiliser jusqu'à 3 écrans simultanément.",
  },
  {
    q: "Puis-je choisir plusieurs écrans ?",
    a: "Oui, selon les offres disponibles, vous pouvez choisir de 1 à 5 écrans / connexions simultanées.",
  },
  {
    q: "Les offres longues durées sont-elles plus avantageuses ?",
    a: "Oui, les offres longues durées permettent généralement de réduire le prix mensuel équivalent.",
  },
  {
    q: "Le parrainage fonctionne-t-il avec toutes les offres ?",
    a: "Oui, lorsqu'un parrainage est applicable, la réduction fixe de 10 € est appliquée quel que soit le montant de l'offre sélectionnée.",
  },
];

const CHANNEL_CATEGORIES = [
  { name: "Sport", icon: "⚽", description: "Tous les grands événements sportifs en direct", channels: ["beIN Sports 1-3", "Canal+ Sport", "Eurosport 1-2", "RMC Sport 1-4", "L'Équipe", "Golf+", "NBA TV", "Tennis Channel"] },
  { name: "Cinéma & Séries", icon: "🎬", description: "Les meilleures chaînes de films et séries", channels: ["Canal+ Cinéma", "Canal+ Séries", "OCS Max", "Ciné+ Premier", "Paramount Channel", "TCM Cinéma", "Action", "Série Club"] },
  { name: "Généralistes Françaises", icon: "🇫🇷", description: "Toutes les chaînes nationales françaises", channels: ["TF1", "France 2", "France 3", "M6", "Arte", "C8", "W9", "TMC", "BFM TV", "CNews"] },
  { name: "Famille & Enfants", icon: "👨‍👩‍👧‍👦", description: "Contenus adaptés pour toute la famille", channels: ["Disney Channel", "Nickelodeon", "Cartoon Network", "Gulli", "Piwi+", "Baby TV", "Canal J", "Boing"] },
  { name: "Documentaires & Culture", icon: "🌍", description: "Découverte, nature, histoire et sciences", channels: ["National Geographic", "Discovery Channel", "History", "Planète+", "Ushuaïa TV", "RMC Découverte", "Animaux"] },
  { name: "Internationales", icon: "🌐", description: "Chaînes du monde entier", channels: ["BBC One", "ITV", "Sky News", "CNN", "RAI 1-3", "TVE", "ZDF", "Al Jazeera", "TV5 Monde"] },
];

const FEATURES = [
  "Plus de 15 000 chaînes TV en direct",
  "Films et séries à la demande en 4K",
  "Jusqu'à 5 connexions simultanées",
  "Compatible Smart TV, mobile, tablette et PC",
  "Activation immédiate après paiement",
  "Paiement 100% sécurisé via Stripe",
];

const DEVICES = [
  { icon: <Tv className="h-7 w-7" />, name: "Smart TV" },
  { icon: <Smartphone className="h-7 w-7" />, name: "Smartphone" },
  { icon: <Tablet className="h-7 w-7" />, name: "Tablette" },
  { icon: <Monitor className="h-7 w-7" />, name: "PC / Mac" },
];

const Offer = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedScreens, setSelectedScreens] = useState(1);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get('/pricing-matrix')
      .then((r) => {
        const o = r.data?.offers || [];
        setOffers(o);
        const def = o.find((x) => x.featured)?.key || o[0]?.key || null;
        setSelectedDuration(def);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const currentOffer = offers.find((o) => o.key === selectedDuration) || null;
  const availableScreens = currentOffer?.screens || [];

  useEffect(() => {
    if (availableScreens.length && !availableScreens.some((s) => s.screens === selectedScreens)) {
      setSelectedScreens(availableScreens[0].screens);
    }
  }, [selectedDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  const screenEntry = availableScreens.find((s) => s.screens === selectedScreens) || availableScreens[0] || null;
  const price = screenEntry?.price || 0;
  const monthly = currentOffer ? price / currentOffer.months : 0;
  const strike = screenEntry?.strike_price || null;
  const economy = strike && strike > price ? strike - price : 0;

  // Structured data SEO
  const allPrices = offers.flatMap((o) => o.screens.map((s) => s.price)).filter(Boolean);
  const structuredData = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://iptv4kfr.fr';
    const faqLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const productLd = allPrices.length ? {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Abonnement IPTV IPTV4KFR',
      description: "Abonnement IPTV flexible : plusieurs durées (1 mois à 3 ans) et jusqu'à 5 connexions simultanées.",
      brand: { '@type': 'Brand', name: 'IPTV4KFR' },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'EUR',
        lowPrice: Math.min(...allPrices),
        highPrice: Math.max(...allPrices),
        offerCount: allPrices.length,
        availability: 'https://schema.org/InStock',
      },
    } : null;
    const breadcrumbLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: origin + '/' },
        { '@type': 'ListItem', position: 2, name: 'Offre', item: origin + '/offre' },
      ],
    };
    return [faqLd, productLd, breadcrumbLd].filter(Boolean);
  }, [allPrices]);

  return (
    <div className="min-h-screen py-12 sm:py-20" data-testid="offer-page">
      <Helmet>
        <title>Abonnement IPTV flexible : 1 mois, 3 mois, 6 mois, 1 an, 2 ans ou 3 ans | IPTV4KFR</title>
        <meta name="description" content="Choisissez votre abonnement IPTV selon vos besoins : plusieurs durées disponibles, jusqu'à 5 connexions simultanées, prix mensuel avantageux et offres longue durée." />
        <link rel="canonical" href={(typeof window !== 'undefined' ? window.location.origin : 'https://iptv4kfr.fr') + '/offre'} />
        <meta property="og:title" content="Abonnement IPTV flexible avec plusieurs durées et connexions simultanées" />
        <meta property="og:description" content="Plusieurs durées (1 mois à 3 ans), jusqu'à 5 connexions simultanées, prix mensuel avantageux sur les longues durées." />
        <meta property="og:type" content="website" />
        {structuredData.map((ld, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
        ))}
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* HERO / INTRO */}
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12 sm:mb-16"
        >
          <span className="inline-flex items-center gap-2 text-[#FF6B35] text-xs font-semibold uppercase tracking-widest mb-4">
            <Sparkles className="h-4 w-4" /> Nos abonnements
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Abonnement IPTV flexible avec plusieurs durées et connexions simultanées
          </h1>
          <p className="text-base md:text-lg text-[#E8E8E8]/80 max-w-3xl mx-auto">
            Choisissez l'accès adapté à vos besoins. Plus vous choisissez une durée longue, plus votre prix mensuel diminue —
            avec jusqu'à 5 connexions simultanées.
          </p>
        </motion.header>

        {/* ====== SÉLECTEUR DYNAMIQUE ====== */}
        <section className="mb-16 sm:mb-24" data-testid="offer-builder" aria-label="Construire mon offre">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Étapes 1 & 2 */}
            <div className="lg:col-span-2 space-y-8">
              {/* Étape 1 — Durée */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span aria-hidden="true" className="w-6 h-6 rounded-full bg-[#FF6B35] text-black text-xs font-bold flex items-center justify-center">1</span>
                  <h2 className="text-base md:text-lg font-bold text-white">Choisissez votre durée</h2>
                </div>
                <p className="text-[#6B6B6E] text-sm mb-4">Les offres longues durées offrent le meilleur prix mensuel.</p>
                {loading ? (
                  <div className="h-40 rounded-xl bg-[#1A1A1D] animate-pulse" />
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="duration-cards">
                    {offers.map((offer) => {
                      const active = selectedDuration === offer.key;
                      return (
                        <button
                          key={offer.key}
                          onClick={() => setSelectedDuration(offer.key)}
                          className={`relative text-left p-4 rounded-xl border transition-all ${
                            active ? 'border-[#FF6B35] bg-[#FF6B35]/10 shadow-lg shadow-[#FF6B35]/10' : 'border-white/10 bg-[#1A1A1D] hover:border-white/30'
                          }`}
                          data-testid={`offer-duration-${offer.key}`}
                        >
                          {(offer.badge || offer.featured) && (
                            <span className="absolute -top-2.5 left-3 bg-[#FF6B35] text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                              {offer.featured && <Star className="h-3 w-3" />}{offer.badge || 'À la une'}
                            </span>
                          )}
                          <div className="flex items-center gap-2 text-white font-bold mt-1">
                            <Calendar className={`h-4 w-4 ${active ? 'text-[#FF6B35]' : 'text-[#6B6B6E]'}`} />
                            {offer.label}
                          </div>
                          <div className="mt-2 text-[#FF6B35] font-bold text-lg">dès {eur(offer.from_price)}&euro;</div>
                          <div className="text-[#6B6B6E] text-xs">soit {eur(offer.from_monthly)}&euro;/mois</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Étape 2 — Écrans */}
              {currentOffer && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span aria-hidden="true" className="w-6 h-6 rounded-full bg-[#FF6B35] text-black text-xs font-bold flex items-center justify-center">2</span>
                    <h2 className="text-base md:text-lg font-bold text-white">Combien de connexions simultanées ?</h2>
                  </div>
                  <p className="text-[#6B6B6E] text-sm mb-4">Chaque écran correspond à une connexion utilisable en même temps.</p>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="screen-cards">
                    {availableScreens.map((s) => {
                      const active = selectedScreens === s.screens;
                      return (
                        <button
                          key={s.screens}
                          onClick={() => setSelectedScreens(s.screens)}
                          className={`relative p-3 rounded-xl border text-center transition-all ${
                            active ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-white/10 bg-[#1A1A1D] hover:border-white/30'
                          }`}
                          data-testid={`offer-screen-${s.screens}`}
                        >
                          {s.badge && (
                            <span className="absolute -top-2 right-2 bg-[#FF6B35] text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">{s.badge}</span>
                          )}
                          <Tv className={`h-5 w-5 mx-auto mb-1 ${active ? 'text-[#FF6B35]' : 'text-[#6B6B6E]'}`} />
                          <div className="text-white text-lg font-bold">{s.screens}</div>
                          <div className="text-[#6B6B6E] text-[10px]">écran{s.screens > 1 ? 's' : ''}</div>
                          <div className="text-[#FF6B35] text-sm font-bold mt-1">{eur(s.price)}&euro;</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Avantages inclus */}
              <div className="bg-[#1A1A1D] rounded-xl border border-white/10 p-5 sm:p-6">
                <h3 className="text-white font-bold mb-4">Tout est inclus dans chaque abonnement</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {FEATURES.map((f, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-[#FF6B35] shrink-0 mt-0.5" />
                      <span className="text-[#E8E8E8] text-sm">{f}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-4 gap-3 mt-5 pt-5 border-t border-white/10">
                  {DEVICES.map((d, i) => (
                    <div key={i} className="flex flex-col items-center gap-2 text-[#B8B8BA]">
                      <span className="text-[#FF6B35]">{d.icon}</span>
                      <span className="text-xs">{d.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Résumé (sticky) */}
            <aside className="lg:sticky lg:top-24 h-fit">
              <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] rounded-2xl border border-[#FF6B35]/30 p-6 shadow-2xl" data-testid="offer-summary">
                <h2 className="text-white font-bold text-lg mb-4">Votre sélection</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#B8B8BA]">Durée</span><span className="text-white font-semibold">{currentOffer?.label || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-[#B8B8BA]">Connexions simultanées</span><span className="text-white font-semibold">{selectedScreens} écran{selectedScreens > 1 ? 's' : ''}</span></div>
                </div>
                <div className="text-center my-5">
                  {strike > 0 && (
                    <div className="text-[#6B6B6E] line-through text-lg" data-testid="offer-strike">{eur(strike)}&euro;</div>
                  )}
                  <div className="text-5xl font-bold bg-gradient-to-r from-[#FF6B35] to-[#F7931E] bg-clip-text text-transparent" data-testid="offer-total-price">
                    {eur(price)}&euro;
                  </div>
                  <div className="text-[#B8B8BA] text-sm mt-1" data-testid="offer-monthly">soit {eur(monthly)}&euro; / mois</div>
                  {economy > 0 && (
                    <div className="inline-block mt-3 bg-[#00FF88]/10 border border-[#00FF88]/30 text-[#00FF88] text-xs font-semibold px-3 py-1 rounded-full" data-testid="offer-economy">
                      Économisez {eur(economy)}&euro;
                    </div>
                  )}
                </div>
                <Link
                  to="/inscription"
                  className="block w-full bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-semibold text-center py-4 rounded-lg hover:shadow-2xl hover:shadow-[#FF6B35]/50 transition-all"
                  data-testid="offer-cta"
                >
                  <span className="inline-flex items-center gap-2">Commander mon accès <ArrowRight className="h-5 w-5" /></span>
                </Link>
                <div className="mt-3 flex items-center justify-center gap-2 text-[#6B6B6E] text-xs">
                  <ShieldCheck className="h-4 w-4 text-[#00FF88]" /> Paiement sécurisé Stripe · activation immédiate
                </div>
                <div className="mt-3 flex items-center justify-center gap-2 text-[#FFB800] text-xs">
                  <Gift className="h-4 w-4" /> Parrainé ? -10&euro; appliqués automatiquement.
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* ====== PACKAGES COMMERCIAUX ====== */}
        {!loading && offers.length > 0 && (
          <section className="mb-16 sm:mb-24" data-testid="offer-packages">
            <div className="text-center mb-10">
              <h2 className="text-base md:text-lg font-bold text-white">Des formules pour chaque besoin</h2>
              <p className="text-[#6B6B6E] text-sm mt-1">Plus vous vous engagez longtemps, plus votre prix mensuel diminue.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {offers.map((offer) => (
                <div
                  key={offer.key}
                  className={`relative rounded-2xl border p-6 transition-all ${
                    offer.featured ? 'border-[#FF6B35] bg-[#FF6B35]/5' : 'border-white/10 bg-[#1A1A1D]'
                  }`}
                  data-testid={`package-${offer.key}`}
                >
                  {(offer.badge || offer.featured) && (
                    <span className="absolute -top-3 left-6 bg-[#FF6B35] text-black text-xs font-bold px-3 py-1 rounded-full">
                      {offer.badge || 'À la une'}
                    </span>
                  )}
                  <h3 className="text-white font-bold text-lg mt-2">{offer.label}</h3>
                  <p className="text-[#6B6B6E] text-sm mt-1 min-h-[40px]">
                    {PACKAGE_DESCRIPTIONS[offer.badge] || "Profitez d'un accès flexible adapté à vos besoins."}
                  </p>
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-[#FF6B35]">dès {eur(offer.from_price)}&euro;</span>
                    <span className="text-[#6B6B6E] text-sm ml-1">soit {eur(offer.from_monthly)}&euro;/mois</span>
                  </div>
                  <button
                    onClick={() => { setSelectedDuration(offer.key); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="mt-5 w-full border border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-black transition-colors py-2.5 rounded-lg font-semibold text-sm"
                    data-testid={`package-select-${offer.key}`}
                  >
                    Choisir cette offre
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ====== CONTENU SEO ====== */}
        <section className="mb-16 sm:mb-24 max-w-4xl mx-auto" data-testid="offer-seo-content">
          <p className="text-[#B8B8BA] text-base leading-relaxed mb-10">
            Nos abonnements IPTV sont conçus pour s'adapter à différents besoins. Vous pouvez choisir une durée courte pour
            tester le service, ou opter pour une offre longue durée afin de bénéficier d'un meilleur prix mensuel. Les
            formules multi-écrans permettent également d'utiliser plusieurs connexions simultanées selon l'offre sélectionnée.
          </p>
          <div className="space-y-8">
            <article>
              <h2 className="text-base md:text-lg font-bold text-white mb-2">Pourquoi choisir un abonnement longue durée ?</h2>
              <p className="text-[#B8B8BA] text-sm leading-relaxed">
                Les abonnements longue durée permettent de réduire le prix mensuel équivalent et d'éviter les renouvellements
                fréquents. Ils sont adaptés aux utilisateurs qui souhaitent profiter du service sur une période prolongée avec
                un tarif plus avantageux.
              </p>
            </article>
            <article>
              <h2 className="text-base md:text-lg font-bold text-white mb-2">Qu'est-ce qu'une connexion simultanée ?</h2>
              <p className="text-[#B8B8BA] text-sm leading-relaxed">
                Une connexion simultanée correspond à un écran actif au même moment. Selon l'offre choisie, vous pouvez
                sélectionner de 1 à 5 connexions simultanées — idéal pour les familles ou les utilisateurs ayant plusieurs appareils.
              </p>
            </article>
            <article>
              <h2 className="text-base md:text-lg font-bold text-white mb-2">Quelle offre choisir ?</h2>
              <p className="text-[#B8B8BA] text-sm leading-relaxed">
                Pour tester le service, une offre courte peut être adaptée. Pour un meilleur rapport qualité-prix, les offres
                1 an, 2 ans ou 3 ans permettent généralement d'obtenir un prix mensuel plus avantageux.
              </p>
            </article>
          </div>
        </section>

        {/* ====== MOSAÏQUE CHAÎNES ====== */}
        <section className="mb-16 sm:mb-24">
          <div className="text-center mb-10">
            <h2 className="text-base md:text-lg font-bold text-white">Plus de 15 000 chaînes disponibles</h2>
            <p className="text-[#6B6B6E] text-sm mt-1">Découvrez notre sélection par catégorie</p>
          </div>
          <div className="space-y-3 max-w-4xl mx-auto">
            {CHANNEL_CATEGORIES.map((category, index) => (
              <div key={index} className="bg-[#1A1A1D] rounded-xl border border-white/10 overflow-hidden">
                <button
                  onClick={() => setExpandedCategory(expandedCategory === index ? null : index)}
                  className="w-full p-5 flex items-center justify-between hover:bg-white/5 transition-colors"
                  data-testid={`channel-cat-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{category.icon}</span>
                    <div className="text-left">
                      <h3 className="text-white font-bold">{category.name}</h3>
                      <p className="text-[#6B6B6E] text-xs">{category.description}</p>
                    </div>
                  </div>
                  {expandedCategory === index ? <ChevronUp className="h-5 w-5 text-[#FF6B35]" /> : <ChevronDown className="h-5 w-5 text-[#6B6B6E]" />}
                </button>
                {expandedCategory === index && (
                  <div className="p-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 border-t border-white/10">
                    {category.channels.map((ch, i) => (
                      <div key={i} className="bg-[#222226] px-3 py-1.5 rounded text-center text-[#E8E8E8] text-xs hover:bg-[#FF6B35]/10 hover:text-[#FF6B35] transition-colors">{ch}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ====== FAQ ====== */}
        <section className="mb-16 sm:mb-20 max-w-3xl mx-auto" data-testid="offer-faq">
          <div className="text-center mb-8">
            <h2 className="text-base md:text-lg font-bold text-white flex items-center justify-center gap-2">
              <HelpCircle className="h-5 w-5 text-[#FF6B35]" /> Questions fréquentes
            </h2>
          </div>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className="bg-[#1A1A1D] rounded-xl border border-white/10 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                  data-testid={`faq-q-${i}`}
                >
                  <span className="text-white font-semibold text-sm pr-4">{item.q}</span>
                  {openFaq === i ? <ChevronUp className="h-5 w-5 text-[#FF6B35] shrink-0" /> : <ChevronDown className="h-5 w-5 text-[#6B6B6E] shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 text-[#B8B8BA] text-sm leading-relaxed">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ====== MAILLAGE INTERNE ====== */}
        <nav className="border-t border-white/10 pt-8 text-center" aria-label="Liens utiles" data-testid="offer-internal-links">
          <p className="text-[#6B6B6E] text-sm mb-4">Pour aller plus loin :</p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/" className="text-[#FF6B35] hover:text-[#F7931E] transition-colors">Accueil</Link>
            <Link to="/faq" className="text-[#FF6B35] hover:text-[#F7931E] transition-colors">Voir les questions fréquentes</Link>
            <Link to="/tutoriels" className="text-[#FF6B35] hover:text-[#F7931E] transition-colors">Tutoriels d'installation</Link>
            <Link to="/devenir-vendeur" className="text-[#FF6B35] hover:text-[#F7931E] transition-colors">Découvrir le parrainage / revendeur</Link>
            <Link to="/cgu" className="text-[#FF6B35] hover:text-[#F7931E] transition-colors">Conditions générales</Link>
            <Link to="/confidentialite" className="text-[#FF6B35] hover:text-[#F7931E] transition-colors">Politique de confidentialité</Link>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Offer;
